// src/context/NotificationContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { Alert, Snackbar } from "@mui/material";
import { useQuery, useQueryClient } from "react-query";
import apiClient from "../utils/apiClient";
import {
  getFCMToken,
  onForegroundMessage,
  getCurrentToken,
  shouldRefreshToken,
  deleteFCMToken,
  sendTokenToServer,
  requestNotificationPermission,
} from "./firebase"; // ✅ CORRECT IMPORT

// Create context with default values
const NotificationContext = createContext({
  notification: null,
  permission: Notification.permission,
  fcmToken: null,
  loading: false,
  requestPermission: async () => {},
  clearNotification: () => {},
  logoutCleanup: async () => {},
  sendTokenToServer: async () => false,
});

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within NotificationProvider");
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState(null);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [loading, setLoading] = useState(false);
  const [fcmToken, setFcmToken] = useState(getCurrentToken());
  const queryClient = useQueryClient();
  const isNotificationFeedInitialized = useRef(false);
  const knownNotificationIdsRef = useRef(new Set());

  // Initialize FCM on mount
  useEffect(() => {
    const initFCM = async () => {
      try {
        console.log("[Notification] Initializing FCM...");

        const token = getCurrentToken();

        if (token && shouldRefreshToken()) {
          console.log("[Notification] Token needs refresh");
          // Don't auto-refresh, wait for user action
        } else if (token) {
          console.log("[Notification] Token is valid");
          setFcmToken(token);
        } else {
          console.log("[Notification] No token found");
        }
      } catch (error) {
        console.error("[Notification] Initialization error:", error);
      }
    };

    initFCM();
  }, []);

  // Listen for foreground messages
  useEffect(() => {
    let unsubscribe = () => {};

    const setupMessageListener = async () => {
      try {
        // onForegroundMessage returns cleanup function
        const cleanup = onForegroundMessage((payload) => {
          console.log("[Notification] New message received:", payload);
          setNotification(payload);

          // Auto-remove notification after 5 seconds
          const timer = setTimeout(() => {
            setNotification(null);
          }, 5000);

          // Store timer for cleanup
          return () => clearTimeout(timer);
        });

        if (typeof cleanup === "function") {
          unsubscribe = cleanup;
        }
      } catch (error) {
        console.error(
          "[Notification] Error setting up message listener:",
          error
        );
      }
    };

    setupMessageListener();

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, []);

  // Fallback real-time watcher: poll notifications and show snackbar for new unread ones
  useQuery({
    queryKey: ["notification-realtime-feed"],
    queryFn: async () => {
      const res = await apiClient.get("/admin/notifications?page=1");
      return res.data;
    },
    enabled: !!sessionStorage.getItem("ecoToken"),
    refetchInterval: 15000,
    refetchIntervalInBackground: true,
    onSuccess: (res) => {
      const list = res?.data?.list || [];
      const unreadList = list.filter((item) => !item.seen);
      const currentIds = new Set(list.map((item) => item.id));

      if (!isNotificationFeedInitialized.current) {
        knownNotificationIdsRef.current = currentIds;
        isNotificationFeedInitialized.current = true;
        return;
      }

      const newUnread = unreadList.filter(
        (item) => !knownNotificationIdsRef.current.has(item.id)
      );

      // Keep ids updated first to avoid duplicate toasts
      knownNotificationIdsRef.current = currentIds;

      if (newUnread.length > 0) {
        const latest = newUnread[0];
        setNotification({
          notification: {
            title: latest.title || "New Notification",
            body: latest.body?.message || latest.message || "",
          },
          data: latest.body || {},
          type: latest.type,
        });

        // Keep notification modal data fresh
        queryClient.invalidateQueries({ queryKey: ["notification"] });
      }
    },
  });

  // Request notification permission
  const requestPermission = useCallback(async () => {
    setLoading(true);
    try {
      console.log("[Notification] Requesting permission...");

      // Check if notifications are supported
      if (!("Notification" in window)) {
        console.warn("[Notification] Notifications not supported");
        throw new Error("Notifications not supported in this browser");
      }

      // Request permission using Firebase function
      const granted = await requestNotificationPermission();

      if (!granted) {
        console.log("[Notification] Permission not granted");
        setPermission(Notification.permission);
        return null;
      }

      // Get FCM token
      console.log("[Notification] Getting FCM token...");
      const token = await getFCMToken(false); // false = don't request permission again

      if (token) {
        console.log("[Notification] Token received");
        setFcmToken(token);
        setPermission(Notification.permission);

        // Get user ID to send token to server
        const userId =
          localStorage.getItem("userId") ||
          sessionStorage.getItem("userId") ||
          JSON.parse(localStorage.getItem("userData") || "{}").id;

        if (userId) {
          await sendTokenToServer(token, userId);
        }

        return token;
      }

      return null;
    } catch (error) {
      console.error("[Notification] Permission request error:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Clear current notification
  const clearNotification = useCallback(() => {
    setNotification(null);
  }, []);

  // Cleanup on logout
  const logoutCleanup = useCallback(async () => {
    try {
      console.log("[Notification] Cleaning up on logout...");
      await deleteFCMToken();
      setFcmToken(null);
      setPermission("default");
      setNotification(null);
      console.log("[Notification] Cleanup complete");
    } catch (error) {
      console.error("[Notification] Logout cleanup error:", error);
    }
  }, []);

  // Send token to server manually
  const sendTokenToServerManual = useCallback(async (token, userId) => {
    try {
      return await sendTokenToServer(token, userId);
    } catch (error) {
      console.error("[Notification] Error sending token:", error);
      return false;
    }
  }, []);

  // Context value
  const value = {
    notification,
    permission,
    fcmToken,
    loading,
    requestPermission,
    clearNotification,
    logoutCleanup,
    sendTokenToServer: sendTokenToServerManual,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}

      {/* Global realtime snackbar */}
      {notification && (
        <Snackbar
          open={Boolean(notification)}
          autoHideDuration={8000}
          onClose={clearNotification}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{
            mt: 2,
            left: "50%",
            transform: "translateX(-50%)",
            "& .MuiSnackbarContent-root": {
              padding: 0,
            },
          }}
        >
          <Alert
            onClose={clearNotification}
            severity="info"
            variant="filled"
            icon={false}
            sx={{
              width: "100%",
              minWidth: { xs: 320, sm: 460 },
              maxWidth: 560,
              borderRadius: "18px",
              boxShadow: "0 20px 50px rgba(2, 6, 23, 0.35)",
              background:
                "linear-gradient(135deg, rgba(37,99,235,0.78) 0%, rgba(29,78,216,0.72) 100%)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              border: "1px solid rgba(255, 255, 255, 0.28)",
              color: "#fff",
              alignItems: "flex-start",
              py: 1.5,
              px: 2,
              "& .MuiAlert-action": {
                marginTop: "2px",
                paddingTop: 0,
              },
              "& .MuiAlert-message": {
                width: "100%",
              },
            }}
          >
            <div className="flex items-start gap-3 w-full">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-xl shrink-0 mt-0.5">
                🔔
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-base font-bold leading-6">
                  {notification.notification?.title ||
                    notification.data?.title ||
                    "New Notification"}
                </div>
                <div className="text-sm sm:text-[15px] opacity-95 leading-6 mt-1 break-words">
                  {notification.notification?.body ||
                    notification.data?.body ||
                    ""}
                </div>
              </div>
            </div>
          </Alert>
        </Snackbar>
      )}
    </NotificationContext.Provider>
  );
};
